// @bun
// src/symbols.ts
var SymbolObservable = typeof Symbol === "function" && Symbol.observable || Symbol.for("@@observable");
if (typeof Symbol === "function") {
  Symbol.observable = SymbolObservable;
}

// src/observable.ts
function getMethod(obj, key) {
  if (obj === null || obj === undefined)
    return;
  const value = obj[key];
  if (value == null)
    return;
  if (typeof value !== "function") {
    throw new TypeError(value + " is not a function");
  }
  return value;
}
function hostReportError(e) {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(() => {
      throw e;
    });
  } else {
    setTimeout(() => {
      throw e;
    });
  }
}
function SubscriptionObserverImpl(subscription) {
  this._subscription = subscription;
}
SubscriptionObserverImpl.prototype = Object.create(Object.prototype);
Object.defineProperties(SubscriptionObserverImpl.prototype, {
  constructor: { value: Object, configurable: true, writable: true },
  closed: {
    get() {
      return this._subscription._closed;
    },
    configurable: true
  },
  next: {
    value: function next(value) {
      const subscription = this._subscription;
      if (subscription._closed)
        return;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "next");
        if (!m)
          return;
        return m.call(observer, value);
      } catch (e) {
        try {
          this.error(e);
        } catch (err) {
          hostReportError(err);
        }
      }
    },
    configurable: true,
    writable: true
  },
  error: {
    value: function error(errorValue) {
      const subscription = this._subscription;
      if (subscription._closed)
        throw errorValue;
      subscription._closed = true;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "error");
        if (m) {
          return m.call(observer, errorValue);
        }
        throw errorValue;
      } finally {
        subscription._cleanup();
      }
    },
    configurable: true,
    writable: true
  },
  complete: {
    value: function complete(value) {
      const subscription = this._subscription;
      if (subscription._closed)
        return;
      subscription._closed = true;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "complete");
        if (m) {
          return m.call(observer, value);
        }
        return;
      } finally {
        subscription._cleanup();
      }
    },
    configurable: true,
    writable: true
  }
});
Object.defineProperty(SubscriptionObserverImpl.prototype.next, "length", { value: 1 });
Object.defineProperty(SubscriptionObserverImpl.prototype.error, "length", { value: 1 });
Object.defineProperty(SubscriptionObserverImpl.prototype.complete, "length", {
  value: 1
});
function Subscription(observer, subscriber) {
  this._observer = observer;
  this._cleanupFn = undefined;
  this._closed = false;
  const subscriptionObserver = new SubscriptionObserverImpl(this);
  try {
    const start = getMethod(observer, "start");
    if (start) {
      start.call(observer, this);
    }
  } catch (e) {
    hostReportError(e);
  }
  if (this._closed)
    return;
  try {
    const cleanup = subscriber(subscriptionObserver);
    if (cleanup != null) {
      if (typeof cleanup !== "function" && typeof cleanup.unsubscribe !== "function") {
        throw new TypeError(cleanup + " is not a function or a subscription");
      }
      this._cleanupFn = cleanup;
      if (this._closed) {
        this._cleanup();
      }
    }
  } catch (e) {
    subscriptionObserver.error(e);
  }
}
Subscription.prototype = Object.create(Object.prototype);
Object.defineProperties(Subscription.prototype, {
  constructor: { value: Object, configurable: true, writable: true },
  closed: {
    get() {
      return this._closed;
    },
    configurable: true
  },
  unsubscribe: {
    value: function unsubscribe() {
      if (this._closed)
        return;
      this._closed = true;
      this._cleanup();
    },
    configurable: true,
    writable: true
  },
  _cleanup: {
    value: function _cleanup() {
      const cleanup = this._cleanupFn;
      if (!cleanup)
        return;
      this._cleanupFn = undefined;
      try {
        if (typeof cleanup === "function") {
          cleanup();
        } else if (cleanup && typeof cleanup.unsubscribe === "function") {
          cleanup.unsubscribe();
        }
      } catch (e) {
        hostReportError(e);
      }
    },
    configurable: true,
    writable: true
  }
});

class Observable {
  _subscriber;
  constructor(subscriber) {
    if (typeof subscriber !== "function") {
      throw new TypeError("Observable initializer must be a function");
    }
    this._subscriber = subscriber;
  }
  subscribe(observerOrNext, error2, complete2) {
    let observer;
    if (typeof observerOrNext === "function") {
      observer = {
        next: observerOrNext,
        error: error2,
        complete: complete2
      };
    } else if (typeof observerOrNext !== "object" || observerOrNext === null) {
      throw new TypeError(observerOrNext + " is not an object");
    } else {
      observer = observerOrNext;
    }
    return new Subscription(observer, this._subscriber);
  }
  [SymbolObservable]() {
    return this;
  }
  static of(...items) {
    const C = typeof this === "function" ? this : Observable;
    return new C((observer) => {
      for (let i = 0;i < items.length; ++i) {
        observer.next(items[i]);
        if (observer.closed)
          return;
      }
      observer.complete();
    });
  }
  static from(x) {
    const C = typeof this === "function" ? this : Observable;
    if (x == null)
      throw new TypeError(x + " is not an object");
    const method = x[SymbolObservable];
    if (method != null) {
      if (typeof method !== "function") {
        throw new TypeError(method + " is not a function");
      }
      const observable = method.call(x);
      if (Object(observable) !== observable) {
        throw new TypeError(observable + " is not an object");
      }
      if (observable.constructor === C) {
        return observable;
      }
      return new C((observer) => observable.subscribe(observer));
    }
    if (Symbol.iterator in x) {
      return new C((observer) => {
        for (const item of x) {
          observer.next(item);
          if (observer.closed)
            return;
        }
        observer.complete();
      });
    }
    throw new TypeError(x + " is not observable");
  }
}

// src/observe.ts
var PROXY_MARKER = Symbol.for("@lasercat/event-emission/proxy");
var ORIGINAL_TARGET = Symbol.for("@lasercat/event-emission/original");
var ARRAY_MUTATORS = new Set([
  "push",
  "pop",
  "shift",
  "unshift",
  "splice",
  "sort",
  "reverse",
  "fill",
  "copyWithin"
]);
function isProxyable(value) {
  return value !== null && typeof value === "object" && !isProxied(value) && !(value instanceof Date) && !(value instanceof RegExp) && !(value instanceof Map) && !(value instanceof Set) && !(value instanceof WeakMap) && !(value instanceof WeakSet) && !(value instanceof Promise) && !(value instanceof Error) && !(value instanceof ArrayBuffer) && !ArrayBuffer.isView(value);
}
function isProxied(value) {
  return typeof value === "object" && value !== null && value[PROXY_MARKER] === true;
}
function isArrayMutator(prop) {
  return typeof prop === "string" && ARRAY_MUTATORS.has(prop);
}
function cloneAlongPath(obj, path) {
  const isArray = Array.isArray(obj);
  const rootClone = isArray ? [...obj] : { ...obj };
  if (!path || isArray) {
    return rootClone;
  }
  const parts = path.split(".");
  const result = rootClone;
  let current = result;
  for (let i = 0;i < parts.length; i++) {
    const key = parts[i];
    const value = current[key];
    if (value !== null && typeof value === "object") {
      current[key] = Array.isArray(value) ? [...value] : { ...value };
      if (i < parts.length - 1) {
        current = current[key];
      }
    } else {
      break;
    }
  }
  return result;
}
function cloneForComparison(obj, strategy, changedPath, deepClone) {
  if (obj === null || typeof obj !== "object")
    return obj;
  switch (strategy) {
    case "shallow":
      return Array.isArray(obj) ? [...obj] : { ...obj };
    case "deep":
      if (deepClone) {
        return deepClone(obj);
      }
      if (typeof structuredClone !== "function") {
        throw new Error("structuredClone is not available in this runtime; provide observe.deepClone, or use cloneStrategy 'path' or 'shallow'.");
      }
      return structuredClone(obj);
    case "path":
      return cloneAlongPath(obj, changedPath);
    default:
      return obj;
  }
}
function computeArrayDiff(method, before, _after, args) {
  switch (method) {
    case "push":
      return { added: args };
    case "pop":
      return { removed: before.length > 0 ? [before[before.length - 1]] : [] };
    case "shift":
      return { removed: before.length > 0 ? [before[0]] : [] };
    case "unshift":
      return { added: args };
    case "splice": {
      const [start, deleteCount, ...items] = args;
      const actualStart = start < 0 ? Math.max(before.length + start, 0) : Math.min(start, before.length);
      const actualDeleteCount = Math.min(deleteCount ?? before.length - actualStart, before.length - actualStart);
      return {
        removed: before.slice(actualStart, actualStart + actualDeleteCount),
        added: items
      };
    }
    case "sort":
    case "reverse":
    case "fill":
    case "copyWithin":
      return {};
    default:
      return {};
  }
}
var proxyRegistry = new WeakMap;
function getContextRegistry(target) {
  let contextMap = proxyRegistry.get(target);
  if (!contextMap) {
    contextMap = new WeakMap;
    proxyRegistry.set(target, contextMap);
  }
  return contextMap;
}
function createArrayMethodInterceptor(array, method, path, context) {
  const original = array[method];
  return function(...args) {
    const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, path, context.options.deepClone);
    const previousItems = [...array];
    const result = original.apply(this, args);
    const { added, removed } = computeArrayDiff(method, previousItems, array, args);
    const methodEventPath = path ? `update:${path}.${method}` : `update:${method}`;
    const arrayEventPath = path ? `update:${path}` : "update:";
    context.eventTarget.dispatchEvent({
      type: methodEventPath,
      detail: {
        method,
        args,
        result,
        added,
        removed,
        current: context.originalRoot,
        previous: previousState
      }
    });
    if (path) {
      context.eventTarget.dispatchEvent({
        type: arrayEventPath,
        detail: {
          value: array,
          current: context.originalRoot,
          previous: previousState
        }
      });
    }
    context.eventTarget.dispatchEvent({
      type: "update",
      detail: {
        current: context.originalRoot,
        previous: previousState
      }
    });
    return result;
  };
}
function createObservableProxyInternal(target, path, context) {
  const contextRegistry = getContextRegistry(target);
  const existing = contextRegistry.get(context);
  if (existing) {
    return existing.proxy;
  }
  const proxy = new Proxy(target, {
    get(obj, prop, receiver) {
      if (prop === PROXY_MARKER)
        return true;
      if (prop === ORIGINAL_TARGET)
        return obj;
      if (typeof prop === "symbol") {
        return Reflect.get(obj, prop, receiver);
      }
      const value = Reflect.get(obj, prop, receiver);
      if (Array.isArray(obj) && isArrayMutator(prop)) {
        return createArrayMethodInterceptor(obj, prop, path, context);
      }
      if (context.options.deep && isProxyable(value)) {
        const nestedPath = path ? `${path}.${prop}` : prop;
        return createObservableProxyInternal(value, nestedPath, context);
      }
      return value;
    },
    set(obj, prop, value, receiver) {
      if (typeof prop === "symbol") {
        return Reflect.set(obj, prop, value, receiver);
      }
      const oldValue = Reflect.get(obj, prop, receiver);
      if (Object.is(oldValue, value)) {
        return true;
      }
      const propPath = path ? `${path}.${prop}` : prop;
      const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, propPath, context.options.deepClone);
      const success = Reflect.set(obj, prop, value, receiver);
      if (success) {
        context.eventTarget.dispatchEvent({
          type: `update:${propPath}`,
          detail: {
            value,
            current: context.originalRoot,
            previous: previousState
          }
        });
        context.eventTarget.dispatchEvent({
          type: "update",
          detail: {
            current: context.originalRoot,
            previous: previousState
          }
        });
      }
      return success;
    },
    deleteProperty(obj, prop) {
      if (typeof prop === "symbol") {
        return Reflect.deleteProperty(obj, prop);
      }
      const propPath = path ? `${path}.${String(prop)}` : String(prop);
      const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, propPath, context.options.deepClone);
      const success = Reflect.deleteProperty(obj, prop);
      if (success) {
        context.eventTarget.dispatchEvent({
          type: `update:${propPath}`,
          detail: {
            value: undefined,
            current: context.originalRoot,
            previous: previousState
          }
        });
        context.eventTarget.dispatchEvent({
          type: "update",
          detail: {
            current: context.originalRoot,
            previous: previousState
          }
        });
      }
      return success;
    }
  });
  contextRegistry.set(context, {
    proxy,
    path
  });
  return proxy;
}
function isEventTarget(obj) {
  return typeof obj === "object" && obj !== null && typeof obj.addEventListener === "function" && typeof obj.removeEventListener === "function" && typeof obj.dispatchEvent === "function";
}
function setupEventForwarding(source, target) {
  const handlers = new Map;
  const sourceAddEventListener = source.addEventListener.bind(source);
  const sourceRemoveEventListener = source.removeEventListener.bind(source);
  const forwardHandler = (type) => (event) => {
    const detail = event.detail ?? event;
    target.dispatchEvent({
      type,
      detail
    });
  };
  const originalAddEventListener = target.addEventListener.bind(target);
  const wrappedAddEventListener = (type, listener, options) => {
    if (!handlers.has(type) && type !== "update" && !type.startsWith("update:")) {
      const handler = forwardHandler(type);
      handlers.set(type, handler);
      sourceAddEventListener(type, handler);
    }
    return originalAddEventListener(type, listener, options);
  };
  target.addEventListener = wrappedAddEventListener;
  return () => {
    target.addEventListener = originalAddEventListener;
    for (const [type, handler] of handlers) {
      sourceRemoveEventListener(type, handler);
    }
    handlers.clear();
  };
}
function isObserved(obj) {
  return isProxied(obj);
}
function getOriginal(proxy) {
  if (!isProxied(proxy)) {
    return proxy;
  }
  return proxy[ORIGINAL_TARGET] ?? proxy;
}
function createObservableProxy(target, eventTarget, options) {
  const resolvedOptions = {
    deep: options?.deep ?? true,
    cloneStrategy: options?.cloneStrategy ?? "path",
    deepClone: options?.deepClone
  };
  const context = {
    eventTarget,
    originalRoot: target,
    options: resolvedOptions
  };
  const proxy = createObservableProxyInternal(target, "", context);
  if (isEventTarget(target)) {
    const cleanupForwarding = setupEventForwarding(target, eventTarget);
    const maybeComplete = eventTarget.complete;
    if (typeof maybeComplete === "function") {
      const originalComplete = maybeComplete.bind(eventTarget);
      let cleaned = false;
      eventTarget.complete = () => {
        if (!cleaned) {
          cleaned = true;
          cleanupForwarding();
        }
        return originalComplete();
      };
    }
  }
  return proxy;
}
// src/errors.ts
class BufferOverflowError extends Error {
  constructor(eventType, bufferSize) {
    super(`Buffer overflow for event type "${eventType}" (max: ${bufferSize})`);
    this.name = "BufferOverflowError";
  }
}
// src/factory.ts
function matchesWildcard(eventType, pattern) {
  if (pattern === "*")
    return true;
  return pattern.endsWith(":*") && eventType.startsWith(pattern.slice(0, -2) + ":");
}
var EVENT_STATE = Symbol("event-emission:event-state");
function createEventTarget(targetOrOpts, opts) {
  if (opts?.observe === true && targetOrOpts && typeof targetOrOpts === "object") {
    const target = targetOrOpts;
    const eventTarget = createEventTargetInternal({
      onListenerError: opts.onListenerError
    });
    const proxy = createObservableProxy(target, eventTarget, {
      deep: opts.deep,
      cloneStrategy: opts.cloneStrategy,
      deepClone: opts.deepClone
    });
    const methodNames = [
      "addEventListener",
      "removeEventListener",
      "dispatchEvent",
      "clear",
      "on",
      "once",
      "removeAllListeners",
      "pipe",
      "addWildcardListener",
      "removeWildcardListener",
      "complete",
      "subscribe",
      "toObservable",
      "events"
    ];
    for (const name of methodNames) {
      Object.defineProperty(proxy, name, {
        value: eventTarget[name],
        writable: false,
        enumerable: false,
        configurable: true
      });
    }
    Object.defineProperty(proxy, "completed", {
      get: () => eventTarget.completed,
      enumerable: false,
      configurable: true
    });
    return proxy;
  }
  return createEventTargetInternal(targetOrOpts);
}
function createEventTargetInternal(opts) {
  const listeners = new Map;
  const wildcardListeners = new Set;
  let isCompleted = false;
  const completionCallbacks = new Set;
  const now = () => typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now();
  const initializeEventState = (state, type, bubbles, cancelable) => {
    state.initializedFlag = true;
    state.stopPropagationFlag = false;
    state.stopImmediatePropagationFlag = false;
    state.canceledFlag = false;
    state.isTrusted = false;
    state.target = null;
    state.currentTarget = null;
    state.eventPhase = 0;
    state.type = type;
    state.bubbles = bubbles;
    state.cancelable = cancelable;
  };
  const setCanceledFlag = (state) => {
    if (state.cancelable && !state.inPassiveListenerFlag) {
      state.canceledFlag = true;
    }
  };
  const createEvent = (type, detail, init) => {
    const state = {
      dispatchFlag: false,
      initializedFlag: true,
      stopPropagationFlag: false,
      stopImmediatePropagationFlag: false,
      canceledFlag: false,
      inPassiveListenerFlag: false,
      composedFlag: Boolean(init?.composed),
      eventPhase: init?.eventPhase ?? 0,
      currentTarget: init?.currentTarget ?? init?.target ?? null,
      target: init?.target ?? null,
      timeStamp: init?.timeStamp ?? now(),
      path: [],
      type,
      bubbles: Boolean(init?.bubbles),
      cancelable: Boolean(init?.cancelable),
      isTrusted: false
    };
    const event = { detail };
    Object.defineProperties(event, {
      type: {
        get: () => state.type,
        enumerable: true,
        configurable: true
      },
      bubbles: {
        get: () => state.bubbles,
        enumerable: true,
        configurable: true
      },
      cancelable: {
        get: () => state.cancelable,
        enumerable: true,
        configurable: true
      },
      cancelBubble: {
        get: () => state.stopPropagationFlag,
        set: (value) => {
          if (value)
            state.stopPropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      composed: {
        get: () => state.composedFlag,
        enumerable: true,
        configurable: true
      },
      currentTarget: {
        get: () => state.currentTarget,
        enumerable: true,
        configurable: true
      },
      defaultPrevented: {
        get: () => state.canceledFlag,
        enumerable: true,
        configurable: true
      },
      eventPhase: {
        get: () => state.eventPhase,
        enumerable: true,
        configurable: true
      },
      isTrusted: {
        get: () => state.isTrusted,
        enumerable: true,
        configurable: true
      },
      returnValue: {
        get: () => !state.canceledFlag,
        set: (value) => {
          if (value === false)
            setCanceledFlag(state);
        },
        enumerable: true,
        configurable: true
      },
      srcElement: {
        get: () => state.target,
        enumerable: true,
        configurable: true
      },
      target: {
        get: () => state.target,
        enumerable: true,
        configurable: true
      },
      timeStamp: {
        get: () => state.timeStamp,
        enumerable: true,
        configurable: true
      },
      composedPath: {
        value: () => state.path.map((entry) => entry.invocationTarget),
        enumerable: true,
        configurable: true
      },
      initEvent: {
        value: (newType, bubbles = false, cancelable = false) => {
          if (state.dispatchFlag)
            return;
          initializeEventState(state, newType, Boolean(bubbles), Boolean(cancelable));
        },
        enumerable: true,
        configurable: true
      },
      preventDefault: {
        value: () => setCanceledFlag(state),
        enumerable: true,
        configurable: true
      },
      stopImmediatePropagation: {
        value: () => {
          state.stopPropagationFlag = true;
          state.stopImmediatePropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      stopPropagation: {
        value: () => {
          state.stopPropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      NONE: { value: 0, enumerable: true, configurable: true },
      CAPTURING_PHASE: { value: 1, enumerable: true, configurable: true },
      AT_TARGET: { value: 2, enumerable: true, configurable: true },
      BUBBLING_PHASE: { value: 3, enumerable: true, configurable: true },
      [EVENT_STATE]: {
        value: state,
        enumerable: false,
        configurable: false
      }
    });
    return event;
  };
  const getEventState = (event) => event[EVENT_STATE];
  const normalizeAddListenerOptions = (options) => {
    if (typeof options === "boolean") {
      return {
        capture: options,
        passive: false,
        once: false,
        signal: null
      };
    }
    return {
      capture: Boolean(options?.capture),
      passive: Boolean(options?.passive),
      once: Boolean(options?.once),
      signal: options?.signal ?? null
    };
  };
  const normalizeCaptureOption = (options) => {
    if (typeof options === "boolean")
      return options;
    return Boolean(options?.capture);
  };
  const removeListenerRecord = (type, record) => {
    if (record.removed)
      return;
    record.removed = true;
    const list = listeners.get(type);
    if (list) {
      const idx = list.indexOf(record);
      if (idx >= 0)
        list.splice(idx, 1);
      if (list.length === 0)
        listeners.delete(type);
    }
    if (record.signal && record.abortHandler) {
      record.signal.removeEventListener("abort", record.abortHandler);
    }
  };
  const handleListenerError = (eventType, error2) => {
    if (eventType === "error")
      return;
    if (opts?.onListenerError) {
      opts.onListenerError(eventType, error2);
      return;
    }
    const errorListeners = listeners.get("error");
    if (errorListeners && errorListeners.length > 0) {
      dispatchEvent({ type: "error", detail: error2 });
    } else {
      throw error2;
    }
  };
  const notifyWildcardListeners = (eventType, event) => {
    if (wildcardListeners.size === 0)
      return;
    for (const rec of Array.from(wildcardListeners)) {
      if (!matchesWildcard(eventType, rec.pattern))
        continue;
      const baseEvent = createEvent(rec.pattern, event.detail, {
        target,
        currentTarget: target,
        eventPhase: 2,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        composed: event.composed
      });
      const wildcardEvent = Object.defineProperties(baseEvent, {
        originalType: { value: eventType, enumerable: true, configurable: true }
      });
      try {
        const fn = rec.fn;
        const res = fn(wildcardEvent);
        if (res && typeof res.then === "function") {
          res.catch((error2) => {
            try {
              handleListenerError(eventType, error2);
            } catch (rethrown) {
              queueMicrotask(() => {
                throw rethrown;
              });
            }
          });
        }
      } catch (error2) {
        handleListenerError(eventType, error2);
      } finally {
        if (rec.once)
          wildcardListeners.delete(rec);
      }
      const state = getEventState(wildcardEvent);
      if (state?.stopImmediatePropagationFlag || state?.stopPropagationFlag) {
        break;
      }
    }
  };
  const addEventListener = (type, listener, options) => {
    if (isCompleted || !listener) {
      return () => {};
    }
    const { capture, passive, once, signal } = normalizeAddListenerOptions(options);
    let list = listeners.get(type);
    if (!list) {
      list = [];
      listeners.set(type, list);
    }
    for (const existing of list) {
      if (existing.original === listener && existing.capture === capture) {
        return () => removeEventListener(type, listener, options);
      }
    }
    const original = listener;
    const callback = typeof listener === "function" ? listener : (event) => listener.handleEvent(event);
    const record = {
      type,
      original,
      callback,
      capture,
      passive,
      once,
      signal,
      removed: false
    };
    list.push(record);
    const unsubscribe2 = () => removeListenerRecord(type, record);
    if (signal) {
      const onAbort = () => removeListenerRecord(type, record);
      record.abortHandler = onAbort;
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted)
        onAbort();
    }
    return unsubscribe2;
  };
  const addWildcardListener = (pattern, listener, options) => {
    if (isCompleted)
      return () => {};
    const opts2 = options ?? {};
    for (const existing of wildcardListeners) {
      if (existing.pattern === pattern && existing.fn === listener) {
        return () => removeWildcardListener(pattern, listener);
      }
    }
    const record = {
      fn: listener,
      pattern,
      once: opts2.once,
      signal: opts2.signal
    };
    wildcardListeners.add(record);
    const unsubscribe2 = () => {
      wildcardListeners.delete(record);
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    };
    if (opts2.signal) {
      const onAbort = () => unsubscribe2();
      record.abortHandler = onAbort;
      opts2.signal.addEventListener("abort", onAbort, { once: true });
      if (opts2.signal.aborted)
        onAbort();
    }
    return unsubscribe2;
  };
  const removeWildcardListener = (pattern, listener) => {
    for (const record of Array.from(wildcardListeners)) {
      if (record.pattern === pattern && record.fn === listener) {
        wildcardListeners.delete(record);
        if (record.signal && record.abortHandler) {
          record.signal.removeEventListener("abort", record.abortHandler);
        }
      }
    }
  };
  const invokeListeners = (eventType, event, phase, listenersSnapshot) => {
    const state = getEventState(event);
    if (!state || state.stopPropagationFlag)
      return;
    state.currentTarget = target;
    state.target = target;
    state.eventPhase = event.AT_TARGET;
    for (const rec of listenersSnapshot) {
      if (rec.removed)
        continue;
      if (phase === "capturing" && !rec.capture)
        continue;
      if (phase === "bubbling" && rec.capture)
        continue;
      if (rec.once)
        removeListenerRecord(rec.type, rec);
      if (rec.passive)
        state.inPassiveListenerFlag = true;
      try {
        const res = rec.callback.call(state.currentTarget, event);
        if (res && typeof res.then === "function") {
          res.catch((error2) => {
            try {
              handleListenerError(eventType, error2);
            } catch (rethrown) {
              queueMicrotask(() => {
                throw rethrown;
              });
            }
          });
        }
      } catch (error2) {
        handleListenerError(eventType, error2);
      } finally {
        if (rec.passive)
          state.inPassiveListenerFlag = false;
      }
      if (state.stopImmediatePropagationFlag)
        break;
    }
  };
  const dispatchEvent = (eventInput) => {
    if (isCompleted)
      return false;
    let event;
    let state;
    if (eventInput && typeof eventInput === "object") {
      state = getEventState(eventInput);
      if (state) {
        event = eventInput;
      } else {
        const input = eventInput;
        if (typeof input.type !== "string") {
          throw new TypeError("Event type must be a string");
        }
        event = createEvent(input.type, input.detail, {
          bubbles: input.bubbles,
          cancelable: input.cancelable,
          composed: input.composed,
          timeStamp: input.timeStamp
        });
        state = getEventState(event);
      }
    } else {
      throw new TypeError("dispatchEvent expects an event object");
    }
    const dispatchState = state ?? getEventState(event);
    if (dispatchState.dispatchFlag || !dispatchState.initializedFlag) {
      const message = "Failed to execute dispatchEvent: event is already being dispatched";
      if (typeof globalThis.DOMException === "function") {
        throw new globalThis.DOMException(message, "InvalidStateError");
      }
      const err = new Error(message);
      err.name = "InvalidStateError";
      throw err;
    }
    dispatchState.isTrusted = false;
    dispatchState.dispatchFlag = true;
    dispatchState.path = [
      {
        invocationTarget: target,
        invocationTargetInShadowTree: false,
        shadowAdjustedTarget: target,
        relatedTarget: null,
        touchTargets: [],
        rootOfClosedTree: false,
        slotInClosedTree: false
      }
    ];
    notifyWildcardListeners(dispatchState.type, event);
    const list = listeners.get(dispatchState.type);
    const snapshot = list ? list.slice() : [];
    invokeListeners(dispatchState.type, event, "capturing", snapshot);
    invokeListeners(dispatchState.type, event, "bubbling", snapshot);
    dispatchState.eventPhase = event.NONE;
    dispatchState.currentTarget = null;
    dispatchState.path = [];
    dispatchState.dispatchFlag = false;
    dispatchState.stopPropagationFlag = false;
    dispatchState.stopImmediatePropagationFlag = false;
    return !dispatchState.canceledFlag;
  };
  const removeEventListener = (type, listener, options) => {
    if (!listener)
      return;
    const capture = normalizeCaptureOption(options);
    const list = listeners.get(type);
    if (!list)
      return;
    for (const record of [...list]) {
      if (record.original === listener && record.capture === capture) {
        removeListenerRecord(type, record);
      }
    }
  };
  const clear = () => {
    for (const [type, list] of Array.from(listeners.entries())) {
      for (const record of [...list]) {
        removeListenerRecord(type, record);
      }
    }
    listeners.clear();
    for (const record of wildcardListeners) {
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    }
    wildcardListeners.clear();
  };
  const on = (type, options) => {
    return new Observable((observer) => {
      let opts2;
      if (typeof options === "boolean") {
        opts2 = { capture: options };
      } else {
        opts2 = options ?? {};
      }
      const handler = opts2.handler;
      const once = opts2.once;
      const eventHandler = (e) => {
        let success = false;
        try {
          if (handler) {
            handler(e);
          }
          observer.next(e);
          success = true;
        } finally {
          if (once && success) {
            observer.complete();
          }
        }
      };
      const errorHandler = (e) => {
        observer.error(e.detail);
      };
      const unsubscribeEvent = addEventListener(type, eventHandler, opts2);
      let unsubscribeError;
      if (opts2.receiveError) {
        unsubscribeError = addEventListener("error", errorHandler, opts2);
      }
      return () => {
        unsubscribeEvent();
        if (unsubscribeError) {
          unsubscribeError();
        }
      };
    });
  };
  const onceMethod = (type, listener, options) => {
    if (typeof options === "boolean") {
      return addEventListener(type, listener, { capture: options, once: true });
    }
    return addEventListener(type, listener, { ...options ?? {}, once: true });
  };
  const removeAllListeners = (type) => {
    if (type !== undefined) {
      const list = listeners.get(type);
      if (list) {
        for (const record of [...list]) {
          removeListenerRecord(type, record);
        }
        listeners.delete(type);
      }
    } else {
      for (const [eventType, list] of Array.from(listeners.entries())) {
        for (const record of [...list]) {
          removeListenerRecord(eventType, record);
        }
      }
      listeners.clear();
      for (const record of wildcardListeners) {
        if (record.signal && record.abortHandler) {
          record.signal.removeEventListener("abort", record.abortHandler);
        }
      }
      wildcardListeners.clear();
    }
  };
  const pipe = (target2, mapFn) => {
    if (isCompleted) {
      return () => {};
    }
    const unsubscribe2 = addWildcardListener("*", (event) => {
      if (mapFn) {
        const mapped = mapFn(createEvent(event.originalType, event.detail, {
          target: target2,
          currentTarget: target2,
          eventPhase: 2,
          bubbles: event.bubbles,
          cancelable: event.cancelable,
          composed: event.composed
        }));
        if (mapped !== null) {
          target2.dispatchEvent(mapped);
        }
      } else {
        target2.dispatchEvent({
          type: event.originalType,
          detail: event.detail
        });
      }
    });
    const completionUnsub = () => {
      unsubscribe2();
    };
    completionCallbacks.add(completionUnsub);
    return () => {
      completionCallbacks.delete(completionUnsub);
      unsubscribe2();
    };
  };
  const complete2 = () => {
    if (isCompleted)
      return;
    isCompleted = true;
    for (const cb of completionCallbacks) {
      try {
        cb();
      } catch (err) {
        try {
          handleListenerError("complete", err);
        } catch {}
      }
    }
    completionCallbacks.clear();
    for (const [eventType, list] of Array.from(listeners.entries())) {
      for (const record of [...list]) {
        removeListenerRecord(eventType, record);
      }
    }
    listeners.clear();
    for (const record of wildcardListeners) {
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    }
    wildcardListeners.clear();
  };
  const subscribe = (type, observerOrNext, error2, completeHandler) => {
    let observer;
    if (typeof observerOrNext === "function") {
      observer = {
        next: observerOrNext,
        error: error2,
        complete: completeHandler
      };
    } else {
      observer = observerOrNext ?? {};
    }
    let closed = false;
    if (isCompleted) {
      if (observer.complete) {
        try {
          observer.complete();
        } catch {}
      }
      return {
        unsubscribe: () => {
          closed = true;
        },
        get closed() {
          return closed || isCompleted;
        }
      };
    }
    const unsub = addEventListener(type, (event) => {
      if (closed)
        return;
      if (observer.next) {
        try {
          observer.next(event);
        } catch (err) {
          if (observer.error) {
            try {
              observer.error(err);
            } catch {}
          }
        }
      }
    });
    const onComplete = () => {
      if (closed)
        return;
      closed = true;
      if (observer.complete) {
        try {
          observer.complete();
        } catch {}
      }
    };
    completionCallbacks.add(onComplete);
    return {
      unsubscribe: () => {
        if (closed)
          return;
        closed = true;
        completionCallbacks.delete(onComplete);
        unsub();
      },
      get closed() {
        return closed || isCompleted;
      }
    };
  };
  const toObservable = () => {
    return new Observable((observer) => {
      if (isCompleted) {
        observer.complete();
        return;
      }
      const wildcardListener = (event) => {
        observer.next(createEvent(event.originalType, event.detail, {
          target,
          currentTarget: target,
          eventPhase: 2,
          bubbles: event.bubbles,
          cancelable: event.cancelable,
          composed: event.composed
        }));
      };
      const unsubscribe2 = addWildcardListener("*", wildcardListener);
      const onComplete = () => {
        observer.complete();
      };
      completionCallbacks.add(onComplete);
      return () => {
        unsubscribe2();
        completionCallbacks.delete(onComplete);
      };
    });
  };
  function events(type, options) {
    if (isCompleted) {
      return {
        [Symbol.asyncIterator]() {
          return this;
        },
        next() {
          return Promise.resolve({
            value: undefined,
            done: true
          });
        },
        return() {
          return Promise.resolve({
            value: undefined,
            done: true
          });
        }
      };
    }
    const signal = options?.signal;
    const bufferSize = options?.bufferSize ?? Infinity;
    const overflowStrategy = options?.overflowStrategy ?? "drop-oldest";
    const buffer = [];
    let resolve = null;
    let done = false;
    let hasOverflow = false;
    let onAbort = null;
    const cleanupAbortListener = () => {
      if (signal && onAbort) {
        signal.removeEventListener("abort", onAbort);
      }
    };
    const unsub = addEventListener(type, (event) => {
      if (done)
        return;
      const typedEvent = event;
      if (resolve) {
        const r = resolve;
        resolve = null;
        r({ value: typedEvent, done: false });
      } else {
        if (buffer.length >= bufferSize && bufferSize !== Infinity) {
          switch (overflowStrategy) {
            case "drop-oldest":
              buffer.shift();
              buffer.push(typedEvent);
              break;
            case "drop-latest":
              break;
            case "throw":
              unsub();
              completionCallbacks.delete(onComplete);
              done = true;
              hasOverflow = true;
              cleanupAbortListener();
              return;
          }
        } else {
          buffer.push(typedEvent);
        }
      }
    });
    const onComplete = () => {
      done = true;
      cleanupAbortListener();
      if (resolve) {
        const r = resolve;
        resolve = null;
        r({ value: undefined, done: true });
      }
    };
    completionCallbacks.add(onComplete);
    if (signal) {
      onAbort = () => {
        done = true;
        completionCallbacks.delete(onComplete);
        unsub();
        if (resolve) {
          const r = resolve;
          resolve = null;
          r({ value: undefined, done: true });
        }
      };
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted)
        onAbort();
    }
    const iterator = {
      [Symbol.asyncIterator]() {
        return this;
      },
      async next() {
        if (buffer.length > 0) {
          return { value: buffer.shift(), done: false };
        }
        if (resolve !== null) {
          return Promise.reject(new Error("Concurrent calls to next() are not supported on this async iterator"));
        }
        return new Promise((_resolve, _reject) => {
          if (hasOverflow) {
            hasOverflow = false;
            _reject(new BufferOverflowError(type, bufferSize));
            return;
          }
          if (done) {
            _resolve({
              value: undefined,
              done: true
            });
            return;
          }
          resolve = _resolve;
        });
      },
      return() {
        if (resolve) {
          const r = resolve;
          resolve = null;
          r({ value: undefined, done: true });
        }
        done = true;
        completionCallbacks.delete(onComplete);
        unsub();
        cleanupAbortListener();
        return Promise.resolve({
          value: undefined,
          done: true
        });
      }
    };
    return iterator;
  }
  const target = {
    addEventListener,
    removeEventListener,
    dispatchEvent,
    clear,
    on,
    once: onceMethod,
    removeAllListeners,
    pipe,
    addWildcardListener,
    removeWildcardListener,
    complete: complete2,
    get completed() {
      return isCompleted;
    },
    subscribe,
    toObservable,
    events
  };
  target[SymbolObservable] = () => {
    return toObservable();
  };
  return target;
}
// src/event-emission.ts
class EventEmission {
  #target;
  constructor() {
    this.#target = createEventTarget();
  }
  addEventListener(type, listener, options) {
    return this.#target.addEventListener(type, listener, options);
  }
  removeEventListener(type, listener, options) {
    this.#target.removeEventListener(type, listener, options);
  }
  dispatchEvent(event) {
    return this.#target.dispatchEvent(event);
  }
  on(type, options) {
    return this.#target.on(type, options);
  }
  once(type, listener, options) {
    return this.#target.once(type, listener, options);
  }
  removeAllListeners(type) {
    this.#target.removeAllListeners(type);
  }
  clear() {
    this.#target.clear();
  }
  pipe(target, mapFn) {
    return this.#target.pipe(target, mapFn);
  }
  addWildcardListener(pattern, listener, options) {
    return this.#target.addWildcardListener(pattern, listener, options);
  }
  removeWildcardListener(pattern, listener) {
    this.#target.removeWildcardListener(pattern, listener);
  }
  subscribe(typeOrObserver, observerOrNext, error2, completeHandler) {
    if (typeof typeOrObserver === "string") {
      return this.#target.subscribe(typeOrObserver, observerOrNext, error2, completeHandler);
    }
    if (typeof typeOrObserver === "function") {
      return this.#target.toObservable().subscribe(typeOrObserver);
    }
    if (typeof typeOrObserver === "object" && typeOrObserver !== null) {
      const maybeObserver = typeOrObserver;
      if (typeof maybeObserver.next === "function" || typeof maybeObserver.error === "function" || typeof maybeObserver.complete === "function") {
        return this.#target.toObservable().subscribe(typeOrObserver);
      }
      return this.#target.toObservable().subscribe({});
    }
    throw new Error("subscribe() requires a string event type, callback function, or observer object");
  }
  toObservable() {
    return this.#target.toObservable();
  }
  [SymbolObservable]() {
    return this.toObservable();
  }
  complete() {
    this.#target.complete();
  }
  get completed() {
    return this.#target.completed;
  }
  events(type, options) {
    return this.#target.events(type, options);
  }
}
export {
  createEventTarget,
  SymbolObservable,
  EventEmission,
  BufferOverflowError
};

//# debugId=60621EDDB80DC90E64756E2164756E21
